import { useState } from "react";
import eventWedding1 from "@/assets/event-wedding1.jpg";
import eventWedding2 from "@/assets/event-wedding2.jpg";

// Event package photos - mix of uploaded and stock event images
const weddingImage = eventWedding2;
const quinceaneraImage = "https://images.unsplash.com/photo-1551504734-5ee1c4a1479b?w=1200&q=90";
const corporateImage = "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=90";
const socialEventImage = "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=90";

type MenuCategory = "packages" | "breakfast" | "lunch" | "entrees" | "desserts";

const Menus = () => {
  const [activeCategory, setActiveCategory] = useState<MenuCategory>("packages");

  const categories: { id: MenuCategory; label: string }[] = [
    { id: "packages", label: "Event Packages" },
    { id: "breakfast", label: "Breakfast" },
    { id: "lunch", label: "Lunch" },
    { id: "entrees", label: "Hot Entrées" },
    { id: "desserts", label: "Desserts & More" },
  ];

  const menuData = {
    packages: [
      {
        name: "Wedding Reception Package",
        image: weddingImage,
        items: [
          "Passed hors d'oeuvres (3 selections)",
          "Dual entrée buffet",
          "Two seasonal sides",
          "House salad with vinaigrette",
          "Artisan bread basket",
          "Beverage station",
          "Cake cutting service",
        ],
      },
      {
        name: "Quinceañera Package",
        image: quinceaneraImage,
        items: [
          "Latin-inspired buffet",
          "Empanadas or mini tamales",
          "Street corn salad",
          "Tres leches cake service",
          "Beverage station",
        ],
      },
      {
        name: "Corporate Luncheon",
        image: corporateImage,
        items: [
          "Choice of entrée: chicken, salmon, or veggie pasta",
          "Two seasonal sides",
          "Mixed greens salad",
          "Cookies or brownies",
          "Beverage station",
        ],
      },
      {
        name: "Social Events / Showers / Rehearsals",
        image: socialEventImage,
        items: [
          "Grazing table or brunch buffet",
          "Mini quiches, pastries, fruit skewers",
          "Coffee & tea station",
          "Optional mimosa bar",
        ],
      },
    ],
    breakfast: [
      {
        name: "Classic Continental",
        items: [
          "Assorted pastries, muffins, croissants",
          "Bagels and breakfast breads",
          "Seasonal fruit display",
        ],
      },
      {
        name: "Southern Breakfast",
        items: [
          "Scrambled eggs with cheese",
          "Buttermilk biscuits",
          "Choice of bacon, sausage, or grits",
          "Orange juice included",
        ],
      },
      {
        name: "Sunrise Waffles & French Toast",
        items: [
          "Belgian waffles or French toast",
          "Maple syrup and fruit toppings",
          "Choice of bacon, sausage, or ham",
        ],
      },
      {
        name: "Chef's Quiche or Frittata",
        items: [
          "Florentine, Meat Lovers, or Three Cheese Vegetarian",
        ],
      },
    ],
    lunch: [
      {
        name: "Signature Boxed Lunch",
        items: ["Choice of sandwich", "Side salad", "Chips", "Dessert"],
      },
      {
        name: "Wrap Lunch",
        items: [
          "Chicken Caesar, steak, or club wrap",
          "Gourmet side salad",
          "Cookie",
        ],
      },
      {
        name: "Executive Chargrilled Chicken Box",
        items: [
          "Herb-marinated chicken",
          "Citrus rice salad or greens",
          "Fresh fruit and roll",
        ],
      },
      {
        name: "CEO Roast Tenderloin Plate",
        items: [
          "Sliced tenderloin with creamy horseradish",
          "Marinated vegetables",
          "Gourmet potato salad",
          "Fresh baguette",
        ],
      },
    ],
    entrees: [
      {
        name: "Chicken Marsala",
        items: [
          "Marsala wine sauce",
          "Buttered noodles",
          "Vegetable medley",
          "Rolls and butter",
        ],
      },
      {
        name: "Lemon Chicken Piccata",
        items: [
          "Lemon-caper sauce over pasta",
          "Seasonal vegetables",
          "Rolls and butter",
        ],
      },
      {
        name: "Grilled Flank Steak",
        items: [
          "Rosemary-seasoned steak",
          "Cheddar smashed potatoes",
          "Fresh broccoli",
          "Rolls and butter",
        ],
      },
      {
        name: "Stuffed Tenderloin",
        items: [
          "Tenderloin stuffed with peppers, spinach, garlic",
          "Rich demi-glace",
          "Twice-baked potato",
          "Rolls and butter",
        ],
      },
    ],
    desserts: [
      {
        name: "Cookie & Brownie Assortment",
        items: [],
      },
      {
        name: "Seasonal Fruit Display",
        items: [],
      },
      {
        name: "Gourmet Dessert Bars",
        items: [],
      },
      {
        name: "Whole Cheesecake",
        items: [],
      },
      {
        name: "Coffee Service",
        items: [],
      },
      {
        name: "Iced Tea or Lemonade",
        items: [],
      },
    ],
  };

  return (
    <section id="menus" className="section-padding bg-background">
      <div className="container-elegant">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-accent font-medium tracking-widest uppercase text-sm mb-3">
            Chef-Driven Cuisine
          </p>
          <h2 className="heading-section text-foreground mb-4">
            Sample Menus & Packages
          </h2>
          <div className="flex justify-center mb-6">
            <span className="text-accent text-3xl">⚜</span>
          </div>
          <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
            From elegant weddings to corporate luncheons, our customizable menus
            blend Southern inspiration with modern flair. All menus accommodate
            dietary needs including vegan, gluten-free, and allergy-sensitive
            options.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? "bg-primary text-primary-foreground shadow-soft"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Menu Cards - Event Packages with images */}
        {activeCategory === "packages" ? (
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {menuData.packages.map((menu, index) => (
              <div
                key={index}
                className="card-elevated overflow-hidden hover:shadow-lg transition-all duration-300 animate-scale-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={menu.image}
                    alt={menu.name}
                    className={`w-full h-full object-cover transition-transform duration-500 hover:scale-105 ${
                      menu.name === "Wedding Reception Package" ? "object-[center_35%]" : ""
                    }`}
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl font-semibold text-foreground mb-4">
                    {menu.name}
                  </h3>
                  <ul className="space-y-2">
                    {menu.items.map((item, idx) => (
                      <li
                        key={idx}
                        className="text-muted-foreground flex items-start gap-2"
                      >
                        <span className="text-accent text-sm mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {menuData[activeCategory].map((menu, index) => (
              <div
                key={index}
                className="card-elevated p-6 hover:shadow-lg transition-all duration-300 animate-scale-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <h3 className="font-serif text-xl font-semibold text-foreground mb-4">
                  {menu.name}
                </h3>
                {menu.items.length > 0 && (
                  <ul className="space-y-2">
                    {menu.items.map((item, idx) => (
                      <li
                        key={idx}
                        className="text-muted-foreground flex items-start gap-2"
                      >
                        <span className="text-accent text-sm mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Call for Pricing */}
        <div className="bg-primary/10 rounded-2xl p-8 max-w-2xl mx-auto text-center border border-primary/20 mt-12">
          <h4 className="font-serif text-2xl font-semibold text-foreground mb-3">
            Menu Pricing
          </h4>
          <p className="text-muted-foreground mb-4">
            All menus are customizable. Contact us for pricing and personalized tasting consultations.
          </p>
          <a
            href="tel:+14045030311"
            className="btn-gold px-8 py-3 rounded-full text-lg font-semibold inline-block hover:scale-105 transition-transform"
          >
            Call for Pricing
          </a>
          <p className="text-muted-foreground text-sm mt-4">
            (404) 503-0311
          </p>
        </div>
      </div>
    </section>
  );
};

export default Menus;
